// WhatIfSimulator — Toggle strategies + Before/After gauge merged
import { useState } from "react";
import { Check, TrendingUp, X } from "lucide-react";

// Simple semicircle gauge for before/after
function MiniGauge({ value, label, color }) {
  const radius = 30;
  const circumference = Math.PI * radius; // Half circle
  const offset = circumference - (Math.min(value, 100) / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-[68px] h-[38px] overflow-hidden">
        <svg viewBox="0 0 68 38" className="w-full h-full">
          <path
            d={`M 4 34 A ${radius} ${radius} 0 0 1 64 34`}
            fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="5" strokeLinecap="round"
          />
          <path
            d={`M 4 34 A ${radius} ${radius} 0 0 1 64 34`}
            fill="none" stroke={color} strokeWidth="5" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 0.6s ease-out" }}
          />
        </svg>
        <div className="absolute bottom-0 left-0 right-0 text-center">
          <span className="text-sm font-bold text-white">{value}</span>
        </div>
      </div>
      <span className="text-[9px] text-white/40 mt-1">{label}</span>
    </div>
  );
}

export function WhatIfSimulator({ valueHeadroom, currentScore, band, valueHeadroomRange }) {
  const strategies = valueHeadroom?.strategies || [];
  const supportive = valueHeadroom?.supportive_strategies || [];
  const allStrategies = [...strategies, ...supportive];

  const [enabled, setEnabled] = useState(() => {
    const init = {};
    allStrategies.forEach(s => { init[s.id] = true; });
    return init;
  });

  if (allStrategies.length === 0) return null;

  const toggle = (id) => setEnabled(prev => ({ ...prev, [id]: !prev[id] }));

  const enabledStrategies = allStrategies.filter(s => enabled[s.id]);
  const disabledCount = allStrategies.length - enabledStrategies.length;

  const enabledPrimary = strategies.filter(s => enabled[s.id]);
  const simLower = enabledPrimary.length > 0
    ? Math.round(enabledPrimary.reduce((sum, s) => sum + s.range.lower, 0) * 0.7)
    : 0;
  const simUpper = enabledPrimary.length > 0
    ? Math.round(enabledPrimary.reduce((sum, s) => sum + s.range.upper, 0) * 0.85)
    : 0;

  const scoreBoost = enabledStrategies.length * 3;
  const simScore = Math.min(currentScore + scoreBoost, 98);

  const enabledWithCosts = enabledStrategies.filter(s => s.implementation_range);
  const costLow = enabledWithCosts.reduce((sum, s) => sum + (s.implementation_range?.lower || 0), 0);
  const costHigh = enabledWithCosts.reduce((sum, s) => sum + (s.implementation_range?.upper || 0), 0);

  return (
    <div data-testid="what-if-simulator">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-4 h-4 text-cipher-gold/70" />
        <p className="text-sm text-white/70 font-medium">What If? Simulator</p>
      </div>

      {/* Before / After gauges */}
      <div className="flex items-center justify-center gap-6 mb-4 bg-black/30 rounded-xl py-4 px-3">
        <MiniGauge value={currentScore} label="Now" color="#6B7280" />
        <div className="text-white/20 text-lg">→</div>
        <MiniGauge value={simScore} label="Potential" color="#006A4D" />
        <div className="flex-1 text-right">
          <p className="text-[9px] text-white/30 uppercase">Value Range</p>
          <p className="text-sm font-bold text-white/80">
            {simLower > 0 ? `£${Math.round(simLower / 1000)}k–£${Math.round(simUpper / 1000)}k` : '—'}
          </p>
          <p className="text-[9px] text-white/30 uppercase mt-1">Investment</p>
          <p className="text-xs font-medium text-white/50">
            {costLow > 0 ? `£${Math.round(costLow / 1000)}k–£${Math.round(costHigh / 1000)}k` : '—'}
          </p>
        </div>
      </div>

      <p className="text-[11px] text-white/40 mb-3">Toggle strategies to see how your potential changes</p>

      {/* Strategy toggles */}
      <div className="space-y-1.5">
        {allStrategies.map((s) => {
          const isOn = enabled[s.id];
          const pct = s.percentage_range;
          return (
            <button
              key={s.id}
              onClick={() => toggle(s.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-all text-left ${
                isOn
                  ? 'border-cipher-green/30 bg-cipher-green/[0.06]'
                  : 'border-white/5 bg-white/[0.02] opacity-50'
              }`}
              data-testid={`whatif-toggle-${s.id}`}
            >
              <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${
                isOn ? 'bg-cipher-green' : 'bg-white/10'
              }`}>
                {isOn && <Check className="w-2.5 h-2.5 text-white" />}
              </div>
              <span className={`text-xs flex-1 ${isOn ? 'text-white/70' : 'text-white/30'}`}>{s.label}</span>
              {pct && (
                <span className={`text-[10px] font-medium ${isOn ? 'text-cipher-green/70' : 'text-white/20'}`}>
                  +{pct.lower}-{pct.upper}%
                </span>
              )}
            </button>
          );
        })}
      </div>

      {disabledCount > 0 && (
        <p className="text-[10px] text-amber-400/50 text-center mt-2">
          {disabledCount} excluded — potential reduced
        </p>
      )}
    </div>
  );
}
